import type { ParameterizedValue } from './extracted-vars-editor';
import type {
  PublishWizardSource,
  TemplatePublishSourceKind,
} from './publish-source';
import type { SlotDescriptionsState } from './slot-description-editor';
import type { ParameterizedTriggerOverride } from './trigger-review-step';

export type PublishRequestVisibility = 'workspace' | 'global';

export interface TemplateRightsMetadata {
  provenance:
    | 'first_party'
    | 'user_generated'
    | 'licensed_partner'
    | 'external_reference_only';
  licenseType: string;
  licenseUrl?: string;
  sourceUrl?: string;
  author: string;
  attribution?: string;
  permissionEvidence?: string;
  containsThirdPartyContent: boolean;
  createdFromExternalTemplate: boolean;
  rightsConfirmed: boolean;
}

export interface PublishRequestMetadata {
  titlesI18n: Record<string, string>;
  description: string;
  descriptionsI18n?: Record<string, string>;
  category: string;
  tags: string[];
  previewImageBlobId?: string;
  templateType: TemplatePublishSourceKind;
  stepInsertType?: string;
}

export interface BuildPublishRequestInput {
  workspaceId: string;
  source: PublishWizardSource;
  metadata: PublishRequestMetadata;
  visibility: PublishRequestVisibility;
  slotDescriptions: SlotDescriptionsState;
  existingTemplateId?: string;
  changelog: string;
  parameterizedValueOverrides: ParameterizedValue[];
  parameterizedTriggerOverrides: ParameterizedTriggerOverride[];
  rightsMetadata: TemplateRightsMetadata;
  expectedRootArtifactRevisionId?: string;
}

export interface BuiltPublishRequest {
  url: string;
  body: Record<string, unknown>;
}

function toApiVisibility(visibility: PublishRequestVisibility) {
  return visibility === 'global' ? 'GLOBAL' : 'WORKSPACE';
}

function buildSlotDescriptionOverrides(
  slotDescriptions: SlotDescriptionsState
) {
  return Object.entries(slotDescriptions)
    .map(([slotId, byLocale]) => ({
      slotId,
      descriptionsI18n: Object.fromEntries(
        Object.entries(byLocale).filter(
          ([, description]) =>
            typeof description === 'string' && description.trim().length > 0
        )
      ),
    }))
    .filter(entry => Object.keys(entry.descriptionsI18n).length > 0);
}

export function buildPublishRequest({
  workspaceId,
  source,
  metadata,
  visibility,
  slotDescriptions,
  existingTemplateId,
  changelog,
  parameterizedValueOverrides,
  parameterizedTriggerOverrides,
  rightsMetadata,
  expectedRootArtifactRevisionId,
}: BuildPublishRequestInput): BuiltPublishRequest {
  const baseMetadata = {
    ...metadata,
    descriptionsI18n: metadata.descriptionsI18n ?? {},
    visibility: toApiVisibility(visibility),
    rightsMetadata,
    ...(expectedRootArtifactRevisionId
      ? { expectedRootArtifactRevisionId }
      : {}),
  };
  const versionTemplateId = existingTemplateId;

  if (source.kind === 'step') {
    return {
      url: `/api/workspace/${workspaceId}/workflow/workflows/${source.workflowId}/publish-node`,
      body: {
        stepId: source.stepId,
        ...baseMetadata,
        slotDescriptionOverrides:
          buildSlotDescriptionOverrides(slotDescriptions),
        ...(versionTemplateId
          ? {
              templateId: versionTemplateId,
              changelog: changelog.trim(),
            }
          : {}),
      },
    };
  }

  if (source.kind === 'doc') {
    return {
      url: versionTemplateId
        ? `/api/workspace/${workspaceId}/marketplace/publish-doc/${versionTemplateId}/version`
        : `/api/workspace/${workspaceId}/marketplace/publish-doc`,
      body: {
        docId: source.docId,
        ...baseMetadata,
        parameterizedValueOverrides,
        ...(versionTemplateId ? { changelog: changelog.trim() } : {}),
      },
    };
  }

  if (source.kind === 'app') {
    return {
      url: versionTemplateId
        ? `/api/workspace/${workspaceId}/marketplace/publish-app/${versionTemplateId}/version`
        : `/api/workspace/${workspaceId}/marketplace/publish-app`,
      body: {
        rootDocId: source.rootDocId,
        ...(source.customAppId ? { customAppId: source.customAppId } : {}),
        ...baseMetadata,
        parameterizedValueOverrides,
        ...(versionTemplateId ? { changelog: changelog.trim() } : {}),
      },
    };
  }

  if (source.kind === 'app_block') {
    return {
      url: versionTemplateId
        ? `/api/workspace/${workspaceId}/marketplace/publish-app-block/${versionTemplateId}/version`
        : `/api/workspace/${workspaceId}/marketplace/publish-app-block`,
      body: {
        rootDocId: source.rootDocId,
        customAppId: source.customAppId,
        appBlockId: source.appBlockId,
        ...baseMetadata,
        parameterizedValueOverrides,
        ...(versionTemplateId ? { changelog: changelog.trim() } : {}),
      },
    };
  }

  if (source.kind === 'workflow') {
    return {
      url: versionTemplateId
        ? `/api/workspace/${workspaceId}/marketplace/publish-workflow/${versionTemplateId}/version`
        : `/api/workspace/${workspaceId}/marketplace/publish-workflow`,
      body: {
        workflowId: source.workflowId,
        ...baseMetadata,
        parameterizedValueOverrides,
        ...(versionTemplateId ? { changelog: changelog.trim() } : {}),
      },
    };
  }

  return {
    url: versionTemplateId
      ? `/api/workspace/${workspaceId}/marketplace/publish/${versionTemplateId}/version`
      : `/api/workspace/${workspaceId}/marketplace/publish`,
    body: {
      automationBlockId: source.automationBlockId,
      ...baseMetadata,
      parameterizedValueOverrides,
      ...(parameterizedTriggerOverrides.length > 0
        ? { parameterizedTriggerOverrides }
        : {}),
      ...(versionTemplateId ? { changelog: changelog.trim() } : {}),
    },
  };
}

export function buildTechnicalRevisionRequest(input: {
  workspaceId: string;
  source: PublishWizardSource;
}): BuiltPublishRequest | null {
  const { workspaceId, source } = input;
  const url = `/api/workspace/${workspaceId}/marketplace/technical-revision`;
  if (source.kind === 'doc') {
    return {
      url,
      body: { templateType: 'doc', docId: source.docId },
    };
  }
  if (source.kind === 'app') {
    return {
      url,
      body: {
        templateType: 'app',
        rootDocId: source.rootDocId,
        ...(source.customAppId ? { customAppId: source.customAppId } : {}),
      },
    };
  }
  if (source.kind === 'app_block') {
    return {
      url,
      body: {
        templateType: 'app_block',
        rootDocId: source.rootDocId,
        customAppId: source.customAppId,
        appBlockId: source.appBlockId,
      },
    };
  }
  return null;
}
